'use client';

import Image from 'next/image';
import { SearchDialog } from '../dialogs/search-dialog';
import { signIn } from 'next-auth/react';
import UserPopover from '../popover/user-popover';
import { Button } from '../ui/button';
import { Heart, UserIcon } from 'lucide-react';
import { Link, usePathname } from '@/i18n/routing';
import { ChangeLang } from '../change-lang';
import { useLocale, useTranslations } from 'next-intl';
import { AddressSheet } from '../dialogs/address-sheet';
import logo from '@/public/logo.svg';
import { useBreadcrumbsStore } from '@/lib/state-stores/breadcrumbStore';
import { Breadcrumbs } from '../breadcrumbs/Breadcrumbs';
import { useLocationStore } from '@/lib/state-stores/locationStore';
import { Session } from 'next-auth';
import { Routes } from '@/lib/routes/routes';
import usePrimeStore from '@/lib/state-stores/primeStore';
import { trackEvent } from '@/lib/amplitude';
import { AmplitudeEvents } from '@/lib/amplitude/events';
import UserCart from './UserCard';
import Container from '../ui/container';
import Typography from '../ui/typography';

export type TSession = {
  session: Session;
};

const HeaderDesktop = ({ session }: TSession) => {
  const { locationDetails } = useLocationStore();
  const { prime } = usePrimeStore();

  const latitude = locationDetails?.location?.latitude ?? '24.7438193';
  const longitude = locationDetails?.location?.longitude ?? '46.7039281';
  const currentPath = usePathname();
  const isStoreRoute = currentPath === '/stores' || /^\/stores\/\d+$/.test(currentPath);
  const isDigitalPath = currentPath === Routes.digitalCards;

  const localActive = useLocale();
  // const isSotresPage = currentPath.includes('/stores');
  const checkIfHome = currentPath === `/`;

  const t = useTranslations('header');

  return (
    <nav className="hidden flex-col items-center justify-start leading-0 lg:flex">
      <Container className="flex items-center justify-between py-12">
        {/* logo and address  */}
        <div className="flex flex-row items-center space-x-6 rtl:space-x-reverse">
          <Link
            href="/"
            className="self-end"
            onClick={() => trackEvent(AmplitudeEvents.HOME_APP_CLICKED)}
          >
            <Image priority src={logo} alt="logo" className="w-20" />
          </Link>

          <AddressSheet
            lang={localActive === 'ar' ? 'ar' : 'en'}
            defaultLatitude={latitude.toString()}
            defaultLongitude={longitude.toString()}
          />

          <ChangeLang />
        </div>

        {/* search bar  */}
        <div
          onClick={() => trackEvent(AmplitudeEvents.SEARCH_BAR_CLICKED)}
          className="flex h-12 w-2/5 items-center justify-center"
        >
          {!isStoreRoute && !isDigitalPath ? (
            <SearchDialog
              isSotresPage={isStoreRoute}
              lang={localActive === 'en' ? 'en' : 'ar'}
              defaultLatitude={latitude.toString()}
              defaultLongitude={longitude.toString()}
              className="flex rounded-xl"
              mediaClass="hidden lg:block"
              dialogClassName="top-[48%] ltr:start-[50.8%] rtl:end-[50.8%] rounded-3xl hidden lg:flex"
            />
          ) : null}
        </div>

        {/* user  */}
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <div className="mt-1 flex items-center gap-4">
            {session && session?.user ? (
              <>
                <UserPopover>
                  <div className="text-primary flex cursor-pointer flex-row items-center space-x-3 text-base font-extrabold transition-opacity hover:opacity-50 rtl:space-x-reverse">
                    <div className="flex max-w-[180px] gap-2">
                      <Typography variant={'h6'} truncate weight={'bold'}>
                        {t('hello')}, {session?.fullName}
                      </Typography>
                      {prime?.primeEnabled && prime?.activeSubscription ? (
                        <Image
                          src={'/assets/prime.svg'}
                          alt="order"
                          width={'20'}
                          height={'21'}
                          className="h-[21px] w-[20px]"
                        />
                      ) : null}
                    </div>
                    <UserIcon />
                  </div>
                </UserPopover>
                <Link
                  href={Routes.wishlist}
                  onClick={() => trackEvent(AmplitudeEvents.MY_WISHLIST_CLICKED)}
                >
                  <Heart />
                </Link>

                {/* cart */}
                <UserCart session={session} />
              </>
            ) : (
              <div className="flex items-center">
                <Button
                  id="login-button"
                  variant={'link'}
                  className="text-primary flex flex-row items-center justify-center space-x-2 rtl:space-x-reverse"
                  onClick={() => signIn('keycloak')}
                >
                  <Typography variant={'h6'} weight={'black'}>
                    {t('login')}
                  </Typography>
                  <UserIcon />
                </Button>
                <Link
                  href={Routes.joinUs}
                  onClick={() => trackEvent(AmplitudeEvents.JOIN_US)}
                  className="text-primary/70 hover:text-primary shrink-0 font-bold hover:underline"
                >
                  <Typography variant={'subtitle1'} weight={'black'}>
                    {t('Join-us')}
                  </Typography>
                </Link>
              </div>
            )}
          </div>
        </div>
      </Container>

      {/* menu  */}
      <ul className="mx-auto hidden w-full flex-row items-center justify-center space-x-6 border-y border-[#E7E7E8] py-4 lg:flex rtl:space-x-reverse">
        <li>
          <Link
            prefetch={true}
            className={`${
              checkIfHome ? 'text-primary' : 'text-primary/50'
            } hover:text-primary flex items-center rounded-lg p-3 pb-2 text-xl leading-0 font-extrabold uppercase transition-all`}
            href="/"
            onClick={() => trackEvent(AmplitudeEvents.HOME_APP_CLICKED)}
          >
            {t('home')}
          </Link>
        </li>
        <li>
          <Link
            prefetch={true}
            className={`${
              currentPath === '/stores' ? 'text-primary' : 'text-primary/50'
            } hover:text-primary flex items-center rounded-lg p-3 pb-2 text-xl leading-0 font-extrabold uppercase transition-all`}
            href={'/stores'}
            onClick={() => trackEvent(AmplitudeEvents.STORES_CLICKED)}
          >
            {t('stores')}
          </Link>
        </li>
      </ul>
    </nav>
  );
};

const HeaderMobile = ({ session }: TSession) => {
  const { locationDetails } = useLocationStore();

  const latitude = locationDetails?.location?.latitude ?? '24.7438193';
  const longitude = locationDetails?.location?.longitude ?? '46.7039281';
  const localActive = useLocale();
  const currentPath = usePathname();
  const isStoreRoute = currentPath === '/stores' || /^\/stores\/\d+$/.test(currentPath);
  // const isSotresPage = currentPath.includes('/stores');
  const isDigitalPath = currentPath === Routes.digitalCards;

  const t = useTranslations('header');

  return (
    <nav className={`z-50 flex flex-col rounded-b py-4 backdrop-blur-3xl lg:hidden`}>
      <Container>
        <div className="flex w-full flex-row items-center justify-between">
          <div className="text-primary flex flex-row items-center space-x-3 text-lg font-extrabold transition-opacity hover:opacity-50 rtl:space-x-reverse">
            <p className="text-base">
              {session && session.user ? (
                <UserPopover>
                  <div className="text-md text-primary flex flex-row items-center space-x-3 font-extrabold transition-opacity hover:opacity-50 rtl:space-x-reverse">
                    <p className={'text-sm'}>
                      {t('hello')}, {session?.fullName} ✨
                    </p>
                  </div>
                </UserPopover>
              ) : null}
            </p>
          </div>

          <AddressSheet
            lang={localActive === 'ar' ? 'ar' : 'en'}
            defaultLatitude={latitude.toString()}
            defaultLongitude={longitude.toString()}
          />
        </div>

        {!isStoreRoute && !isDigitalPath ? (
          <SearchDialog
            lang={localActive === 'en' ? 'en' : 'ar'}
            isSotresPage={isStoreRoute}
            className="mt-2 flex rounded-sm px-2"
            mediaClass=" block"
            dialogClassName="top-[46%] ltr:start-[50%] rtl:end-[50%] rounded-2xl flex"
            defaultLatitude={latitude.toString()}
            defaultLongitude={longitude.toString()}
          />
        ) : null}
      </Container>
    </nav>
  );
};
const Navbar = ({ session }: TSession) => {
  const { breadcrumbItems } = useBreadcrumbsStore();

  return (
    <>
      <HeaderDesktop session={session} />
      <HeaderMobile session={session} />

      <div className="mx-auto flex h-full w-full max-w-[1500px] flex-row overflow-hidden">
        {breadcrumbItems && <Breadcrumbs linksData={breadcrumbItems} />}
      </div>
    </>
  );
};

export default Navbar;
